import { useEffect, useMemo } from 'react';
import { Modal, Form, Input, Button, Divider, Alert } from 'antd';
import PermissionPicker from './PermissionPicker';
import type { RoleGroup, RoleGroupFormValues } from './types';
import { splitPermissions } from '../../../config/permissionModules';

interface Props {
    open: boolean;
    mode: 'add' | 'edit';
    /** Sửa: nhóm đang sửa. Thêm: dữ liệu điền sẵn (sao chép từ nhóm khác), có thể bỏ trống. */
    initialValue?: Pick<RoleGroup, 'name' | 'description' | 'permissions'> | null;
    loading?: boolean;
    onCancel: () => void;
    onSubmit: (values: RoleGroupFormValues) => void;
}

const emptyValues: RoleGroupFormValues = {
    name: '',
    description: '',
    permissions: [],
};

export default function PermissionGroupModal({
    open,
    mode,
    initialValue,
    loading,
    onCancel,
    onSubmit,
}: Props) {
    const [form] = Form.useForm<RoleGroupFormValues>();

    const defaults = useMemo<RoleGroupFormValues>(() => {
        if (initialValue) {
            return {
                name: initialValue.name,
                description: initialValue.description ?? '',
                permissions: initialValue.permissions ?? [],
            };
        }
        return emptyValues;
    }, [initialValue]);

    // Quyền cũ (module/action đã bỏ khỏi danh mục): không hiện trong picker, được dọn khi lưu
    const staleKeys = useMemo(() => splitPermissions(defaults.permissions).stale, [defaults]);

    useEffect(() => {
        if (open) form.setFieldsValue(defaults);
    }, [open, defaults, form]);

    const handleClose = () => {
        form.resetFields();
        onCancel();
    };

    const handleFinish = (values: RoleGroupFormValues) => {
        onSubmit({
            ...values,
            description: values.description?.trim() || '',
            permissions: splitPermissions(values.permissions ?? []).valid,
        });
    };

    return (
        <Modal
            title={mode === 'add' ? 'Thêm nhóm quyền' : `Sửa nhóm quyền: ${initialValue?.name ?? ''}`}
            open={open}
            onCancel={handleClose}
            footer={null}
            destroyOnClose
            width={900}
            centered
            maskClosable={false}
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={defaults}
                onFinish={handleFinish}
            >
                <div className="grid grid-cols-1 gap-x-4">
                    <Form.Item
                        label="Tên nhóm quyền"
                        name="name"
                        rules={[{ required: true, message: 'Vui lòng nhập tên nhóm quyền' }]}
                    >
                        <Input placeholder="VD: Trưởng phòng kinh doanh" />
                    </Form.Item>
                </div>

                <Form.Item label="Mô tả" name="description">
                    <Input.TextArea
                        rows={2}
                        placeholder="Mô tả ngắn về mục đích của nhóm quyền này"
                        maxLength={255}
                        showCount
                    />
                </Form.Item>

                <Divider className="my-3" orientation="left" orientationMargin={0}>
                    <span className="text-sm font-semibold">Phân quyền theo module</span>
                </Divider>

                {staleKeys.length > 0 && (
                    <Alert
                        type="warning"
                        showIcon
                        className="mb-3"
                        message={`Nhóm này còn ${staleKeys.length} quyền cũ không còn dùng trong hệ thống`}
                        description={
                            <>
                                <div className="font-mono text-xs">{staleKeys.join(', ')}</div>
                                <div className="mt-1">
                                    Các quyền này không còn tác dụng và sẽ được xóa khỏi nhóm khi bạn lưu.
                                </div>
                            </>
                        }
                    />
                )}

                <Form.Item
                    name="permissions"
                    rules={[
                        {
                            validator: (_, v: string[]) =>
                                splitPermissions(v ?? []).valid.length > 0
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('Vui lòng chọn ít nhất 1 quyền')),
                        },
                    ]}
                >
                    <PermissionPickerField />
                </Form.Item>

                <div className="flex justify-end gap-2 pt-2 border-t">
                    <Button onClick={handleClose}>Hủy</Button>
                    <Button type="primary" htmlType="submit" loading={loading}
                        className="!bg-[#3e91ab] hover:!bg-[#357f96]">
                        {mode === 'add' ? 'Tạo nhóm quyền' : 'Lưu thay đổi'}
                    </Button>
                </div>
            </Form>
        </Modal>
    );
}

/**
 * Wrapper để PermissionPicker dùng được với antd Form.Item
 * (Form sẽ tự inject value + onChange).
 */
function PermissionPickerField({
    value,
    onChange,
}: {
    value?: string[];
    onChange?: (next: string[]) => void;
}) {
    return (
        <PermissionPicker
            value={value ?? []}
            onChange={(next) => onChange?.(next)}
        />
    );
}
